import React, { useState } from "react";
import cx from "classnames";
import { useLayout } from "../../../theme/LayoutContext";
import {
  NavbarTypes,
  SidebarTypes,
  SIDEBAR_COLORS,
  NAVBAR_COLORS,
} from "../../../theme/layoutConstants";
import CustomColorPicker from "../ColorPicker/ColorPicker";
import Widget from "../Widget/Widget";
import { persistThemeSetting, THEME_DEFAULTS } from "../../utils/theme";
import s from "./Helper.module.scss";

interface CogIconProps {
  className?: string;
}

function CogIcon({ className }: CogIconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96a7.2 7.2 0 0 0-1.62-.94l-.36-2.54A.48.48 0 0 0 14 3h-4a.48.48 0 0 0-.48.41l-.36 2.54c-.59.24-1.13.56-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.65 8.87a.49.49 0 0 0 .12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L2.77 14.52a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.3.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h4c.24 0 .44-.17.48-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.49.49 0 0 0-.12-.61l-2.03-1.58zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z"
      />
    </svg>
  );
}

function Helper() {
  const {
    dashboardTheme,
    navbarColor,
    navbarType,
    sidebarType,
    changeTheme,
    changeNavbarColor,
    navbarTypeToggle,
    sidebarTypeToggle,
  } = useLayout();

  const [isOpened, setIsOpened] = useState<boolean>(false);

  const toggle = () => setIsOpened((v) => !v);

  const handleChangeTheme = (state: string) => {
    persistThemeSetting("dashboardTheme", state);
    changeTheme(state);
  };

  const sidebarColor = dashboardTheme || THEME_DEFAULTS.dashboardTheme;

  return (
    <div className={cx(s.themeHelper, { [s.themeHelperOpened]: isOpened })}>
      <button
        type="button"
        className={cx(s.themeHelperBtn, "helper-button")}
        onClick={toggle}
        aria-label="Open theme settings"
      >
        <span className={s.themeHelperSpinner}>
          <CogIcon className={s.cogLarge} />
          <CogIcon className={s.cogSmall} />
        </span>
      </button>
      <Widget className={s.themeHelperContent}>
        <div className={s.helperHeader}>
          <h5 className="m-0">Theme</h5>
          <button
            type="button"
            className={s.closeBtn}
            onClick={toggle}
            aria-label="Close theme settings"
          >
            ×
          </button>
        </div>

        <div className="theme-settings">
          <h5 className="navbar-type-switcher">Navbar Type</h5>
          <div className="row">
            <div className="abc-radio col-auto">
              <input
                onChange={() => navbarTypeToggle(NavbarTypes.STATIC)}
                type="radio"
                checked={navbarType === NavbarTypes.STATIC}
                name="navbar-type"
                id="navbar_static"
              />
              <label htmlFor="navbar_static">Static</label>
            </div>
            <div className="abc-radio col-auto">
              <input
                onChange={() => navbarTypeToggle(NavbarTypes.FLOATING)}
                type="radio"
                checked={navbarType === NavbarTypes.FLOATING}
                name="navbar-type"
                id="navbar_floating"
              />
              <label htmlFor="navbar_floating">Floating</label>
            </div>
          </div>

          <h5 className="mt-4 navbar-color-picker">Navbar Color</h5>
          <p className={s.hint}>Soft pastels pair best with purple / teal sidebars.</p>
          <CustomColorPicker
            colors={NAVBAR_COLORS}
            activeColor={navbarColor}
            updateColor={changeNavbarColor}
            customizationItem="navbar"
          />

          <h5 className="mt-4 sidebar-type-switcher">Sidebar Type</h5>
          <div className="row">
            <div className="abc-radio col-auto">
              <input
                type="radio"
                onChange={() => sidebarTypeToggle(SidebarTypes.TRANSPARENT)}
                checked={sidebarType === SidebarTypes.TRANSPARENT}
                name="sidebar-type"
                id="sidebar_transparent"
              />
              <label htmlFor="sidebar_transparent">Transparent</label>
            </div>
            <div className="abc-radio col-auto">
              <input
                type="radio"
                onChange={() => sidebarTypeToggle(SidebarTypes.SOLID)}
                checked={sidebarType === SidebarTypes.SOLID}
                name="sidebar-type"
                id="sidebar_solid"
              />
              <label htmlFor="sidebar_solid">Solid</label>
            </div>
          </div>

          <h5 className="mt-4 sidebar-color-picker">Sidebar Color</h5>
          <p className={s.hint}>Try purple, burgundy, or teal for a richer look.</p>
          <CustomColorPicker
            colors={SIDEBAR_COLORS}
            activeColor={sidebarColor}
            updateColor={handleChangeTheme}
            customizationItem="sidebar"
          />
        </div>
      </Widget>
    </div>
  );
}

export default Helper;
